import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { defer, type Observable, of } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { type VisibilityExpressionState } from '@blaze-shared/datasets-domain';
import { type DataBindingSourceInfo } from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import {
  type AiChildGroup,
  type ElementHandler,
  getElement,
  getInitialProps,
  IconResolverService,
  upsertElement,
} from '../../../ai';

import {
  type CollapsibleContainerContext,
  type CollapsibleContainerDefinition,
} from './collapsible-container.definition';

const DEFAULT_CLOSED_PROPS: PartialPropsOf<'collapsibleContainer'> = {
  data: [
    {
      key: 'opened',
      enabled: true,
      dataSource: {
        sourceType: 'expression',
        expression: [
          {
            state: 'close' as unknown as VisibilityExpressionState,
            columns: '',
            operator: ComparisonOperator.Eq,
            value: '',
          },
        ],
      } satisfies Partial<DataBindingSourceInfo> as DataBindingSourceInfo,
    },
  ],
  content: {
    behavior: {
      states: ['close'],
    },
  },
};

export class CollapsibleContainerElementHandler implements ElementHandler<'collapsibleContainer'> {
  private readonly store = inject(Store);
  private readonly iconResolverService = inject(IconResolverService);

  readonly type = 'collapsibleContainer';

  getChildGroups(element: CollapsibleContainerDefinition): AiChildGroup[] {
    return element.children?.length ? [{ slotId: undefined, children: element.children }] : [];
  }

  prepare(context: CollapsibleContainerContext): Observable<CollapsibleContainerContext> {
    if (context.element.iconName) {
      context.element.iconName = this.iconResolverService.resolveFullIconName(
        context.element.iconName,
      );
    }

    return of(context);
  }

  process(context: CollapsibleContainerContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'collapsibleContainer'> = getInitialProps<'collapsibleContainer'>(
      draft,
      context.element.id,
      DEFAULT_CLOSED_PROPS,
    );

    if (context.element.title) {
      props = applyPropsPatch(props, {
        content: {
          header: {
            title: context.element.title,
          },
        },
      });
    }

    if (context.element.iconName) {
      props = applyPropsPatch(props, {
        content: {
          header: {
            icon: context.element.iconName,
          },
        },
      });
    }

    if (context.element.showCollapseIcon !== undefined) {
      props = applyPropsPatch(props, {
        content: {
          header: {
            showChevron: context.element.showCollapseIcon,
          },
        },
      });
    }

    // should be closed by default
    // if (context.element.openByDefault !== undefined) {
    //   props = applyPropsPatch(props, {
    //     content: {
    //       behavior: {
    //         states: [context.element.openByDefault ? 'open' : 'close'],
    //       },
    //     },
    //   });
    // }

    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, 'collapsibleContainer');

    element.width = {
      mode: 'full',
    };

    element.height = {
      mode: 'auto',
    };
  }

  finalize(element: CanvasElementFrom<'collapsibleContainer'>): Observable<void> {
    return defer(() => {
      this.store.dispatch(
        runtimeActions.updateElementMeta({
          elementId: element.id,
          meta: {
            renderFeatures: {
              autoHeight: false,
            },
          },
        }),
      );

      return of(undefined);
    });
  }
}
